import React, { useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ReactGA from "react-ga4";
import { DataProvider } from "./context/DataContext";
import Loading from "./components/Loading";
import ProtectedRoute from "./components/ProtectedRoute";
import WhatsAppButton from "./components/WhatsAppButton";
import PromoBanner from "./components/PromoBanner";
import Analytics from "./components/Analytics";
import AiAssistant from "./components/AiAssistant/AiAssistant";
import Canonical from "./components/SEO/Canonical";

// Static Import for Critical Path (Optimizes LCP)
import Home from "./pages/Home";

// Lazy Load Pages
const PetrolPump = lazy(() => import("./pages/PetrolPump"));
const CommissionAgent = lazy(() => import("./pages/CommissionAgent"));
const LibrarySystem = lazy(() => import("./pages/LibrarySystem"));
const SystemCare = lazy(() => import("./pages/SystemCare"));
const RetailProWeb = lazy(() => import("./pages/RetailProWeb"));
const SchoolPro = lazy(() => import("./pages/SchoolPro"));
const AccountsPro = lazy(() => import("./pages/AccountsPro"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const ResellerLogin = lazy(() => import("./pages/ResellerLogin"));
const ResellerDashboard = lazy(() => import("./pages/ResellerDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
const CustomerLogin = lazy(() => import("./pages/CustomerLogin"));
const GlobalServices = lazy(() => import("./pages/GlobalServices"));
const Tutorials = lazy(() => import("./pages/Tutorials"));
const Careers = lazy(() => import("./pages/Careers"));
const ToolsHub = lazy(() => import("./pages/ToolsHub"));
const InvoiceGenerator = lazy(() => import("./pages/tools/InvoiceGenerator"));
const CommissionCalculator = lazy(
  () => import("./pages/tools/CommissionCalculator"),
);
const HealthCheckup = lazy(() => import("./pages/tools/HealthCheckup"));
const AiTranscriber = lazy(() => import("./pages/tools/AiTranscriber"));
const BarcodeGenerator = lazy(() => import("./pages/tools/BarcodeGenerator"));
const SmartRetailCalculator = lazy(
  () => import("./pages/tools/SmartRetailCalculator"),
);
const BuildVsBuyCalculator = lazy(
  () => import("./pages/tools/BuildVsBuyCalculator"),
);
const SaaSPricingVisualizer = lazy(
  () => import("./pages/tools/SaaSPricingVisualizer"),
);
const WhyManualInvoicesKillBusiness = lazy(
  () => import("./pages/blog/WhyManualInvoicesKillBusiness"),
);
const WhyManualBarcodesRuinBusiness = lazy(
  () => import("./pages/blog/WhyManualBarcodesRuinBusiness"),
);
const TheOutsourcingTruth = lazy(
  () => import("./pages/blog/TheOutsourcingTruth"),
);
const AiSchoolManagement = lazy(
  () => import("./pages/blog/AiSchoolManagement"),
);
const PaperFreeExams = lazy(() => import("./pages/blog/PaperFreeExams"));
const StopGuessingProfits = lazy(
  () => import("./pages/blog/StopGuessingProfits"),
);
const SchoolFeeVouchersGuide = lazy(
  () => import("./pages/blog/SchoolFeeVouchersGuide"),
);
const ChoosingSoftwareHouseGuide = lazy(
  () => import("./pages/blog/ChoosingSoftwareHouseGuide"),
);
const SchoolInvestmentROI = lazy(
  () => import("./pages/blog/SchoolInvestmentROI"),
);
const BlogHub = lazy(() => import("./pages/BlogHub"));
const SystemCareOptimizationGuide = lazy(
  () => import("./pages/blog/SystemCareOptimizationGuide"),
);
const SystemCareProPricing = lazy(() => import("./pages/SystemCareProPricing"));
const SchoolProPricing = lazy(
  () => import("./pages/products/SchoolProPricing"),
);
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const ReturnPolicy = lazy(() => import("./pages/ReturnPolicy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const SoftwareFactory = lazy(
  () => import("./pages/case-studies/SoftwareFactory"),
);
const TypingTutor = lazy(() => import("./pages/tools/TypingTutor"));
const Changelog = lazy(() => import("./pages/Changelog"));
const DistroPro = lazy(() => import("./pages/DistroPro"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const FAQ = lazy(() => import("./pages/FAQ"));

// Replace with your Measurement ID (G-XXXXXXXXXX)
const TRACKING_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || "G-XXXXXXXXXX";

ReactGA.initialize(TRACKING_ID);

// Component to track page views
const RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname + location.search,
    });
  }, [location]);

  return null;
};

// Update Checker Component (Triggers on every route change)
const UpdateChecker = () => {
  const location = useLocation();

  const checkUpdate = async () => {
    if (import.meta.env.MODE === "development") return;

    try {
      const response = await fetch(`/version.json?t=${new Date().getTime()}`, {
        cache: "no-store",
      });
      const data = await response.json();
      const serverVersion = data.version;
      const localVersion = import.meta.env.VITE_APP_VERSION;

      if (serverVersion && localVersion && serverVersion !== localVersion) {
        const url = new URL(window.location.href);
        const currentUrlVersion = url.searchParams.get("v");

        if (currentUrlVersion !== serverVersion) {
          console.log(
            `New version detected: ${serverVersion}. Local version: ${localVersion}. Updating...`,
          );
          url.searchParams.set("v", serverVersion);
          window.location.href = url.toString();
        }
      }
    } catch (error) {
      console.warn("Update check failed:", error);
    }
  };

  useEffect(() => {
    checkUpdate();
  }, [location.pathname]); // Trigger on every page change

  return null;
};

function App() {
  return (
    <HelmetProvider>
      <DataProvider>
        <Router>
          <Canonical />
          <Analytics />
          <RouteTracker />
          <UpdateChecker />
          {/* <PromoBanner /> */}
          <WhatsAppButton />
          {/* <AiAssistant /> */}
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/products/petrol-pump" element={<PetrolPump />} />
              <Route
                path="/products/commission-agent"
                element={<CommissionAgent />}
              />
              <Route
                path="/products/library-system"
                element={<LibrarySystem />}
              />
              <Route path="/products/system-care" element={<SystemCare />} />
              <Route
                path="/products/retail-pro-web"
                element={<RetailProWeb />}
              />
              <Route
                path="/products/system-care/pricing"
                element={<SystemCareProPricing />}
              />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/products/accounts-pro" element={<AccountsPro />} />
              <Route path="/products/school-pro" element={<SchoolPro />} />
              <Route
                path="/products/school-pro/pricing"
                element={<SchoolProPricing />}
              />
              <Route path="/tools" element={<ToolsHub />} />
              <Route
                path="/tools/invoice-generator"
                element={<InvoiceGenerator />}
              />
              <Route
                path="/tools/commission-calculator"
                element={<CommissionCalculator />}
              />
              <Route path="/tools/health-checkup" element={<HealthCheckup />} />
              <Route
                path="/tools/barcode-generator"
                element={<BarcodeGenerator />}
              />
              <Route
                path="/tools/smart-retail-calculator"
                element={<SmartRetailCalculator />}
              />
              <Route
                path="/tools/build-vs-buy-calculator"
                element={<BuildVsBuyCalculator />}
              />
              <Route
                path="/tools/saas-pricing-visualizer"
                element={<SaaSPricingVisualizer />}
              />
              <Route path="/tools/ai-transcriber" element={<AiTranscriber />} />
              <Route
                path="/tools/online-typing-tutor"
                element={<TypingTutor />}
              />
              <Route
                path="/blog/why-manual-invoices-kill-business"
                element={<WhyManualInvoicesKillBusiness />}
              />
              <Route
                path="/blog/the-outsourcing-truth-pakistan-2026"
                element={<TheOutsourcingTruth />}
              />
              <Route
                path="/blog/ai-school-management-2026"
                element={<AiSchoolManagement />}
              />
              <Route
                path="/blog/paper-free-exams"
                element={<PaperFreeExams />}
              />
              <Route
                path="/blog/how-to-generate-school-fee-vouchers"
                element={<SchoolFeeVouchersGuide />}
              />
              <Route
                path="/blog/why-manual-barcodes-ruin-business"
                element={<WhyManualBarcodesRuinBusiness />}
              />
              <Route
                path="/blog/stop-guessing-profits"
                element={<StopGuessingProfits />}
              />
              <Route
                path="/blog/choosing-software-house-pakistan"
                element={<ChoosingSoftwareHouseGuide />}
              />
              <Route
                path="/blog/school-investment-roi-guide-2026"
                element={<SchoolInvestmentROI />}
              />
              <Route path="/blog" element={<BlogHub />} />
              <Route
                path="/blog/why-pc-is-slow-and-how-to-fix"
                element={<SystemCareOptimizationGuide />}
              />
              <Route path="/services" element={<GlobalServices />} />
              <Route path="/tutorials" element={<Tutorials />} />
              <Route path="/reseller/login" element={<ResellerLogin />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/reseller/dashboard"
                element={
                  <ProtectedRoute requiredRole="reseller">
                    <ResellerDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="/careers" element={<Careers />} />
              <Route path="/return-policy" element={<ReturnPolicy />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/customer/login" element={<CustomerLogin />} />
              <Route
                path="/my-account"
                element={
                  <ProtectedRoute requiredRole="client">
                    <ClientPortal />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/case-studies/software-factory"
                element={<SoftwareFactory />}
              />
              <Route path="/changelog" element={<Changelog />} />
              <Route path="/products/distro-pro" element={<DistroPro />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/faq" element={<FAQ />} />
            </Routes>
          </Suspense>
        </Router>
      </DataProvider>
    </HelmetProvider>
  );
}

export default App;
